import moment from 'moment'
import { DataTable, DataTableMessageRow } from 'components/DataTable'
import { Button } from 'components/Button'
import { ScheduledReservation, TTag } from 'types'
import { TAutoReplyMessageAsset } from 'api'
import { DATE_FORMAT, sendAllMessageStrings } from '../../../constants'
import { getDraftPreviewLabel } from '../Drafts/draftPreview'
import { getReservationRecipientLabel } from './reservationRecipient'
import { formatScheduledDateTime } from './reservationScheduledDateTime'

type ReservationsTableProps = {
  reservations: ScheduledReservation[]
  assets: TAutoReplyMessageAsset[]
  tags: TTag[]
  loading: boolean
  onEdit: (reservationId: string) => void
  onCancel: (reservationId: string) => void
  onPreview: (reservation: ScheduledReservation) => void
}

const COLUMN_COUNT = 8

export function ReservationsTable({
  reservations,
  assets,
  tags,
  loading,
  onEdit,
  onCancel,
  onPreview,
}: ReservationsTableProps) {
  let body: JSX.Element | JSX.Element[]

  if (loading) {
    body = (
      <DataTableMessageRow
        colSpan={COLUMN_COUNT}
        message={sendAllMessageStrings.reservations.loading}
      />
    )
  } else if (reservations.length === 0) {
    body = (
      <DataTableMessageRow
        colSpan={COLUMN_COUNT}
        message={sendAllMessageStrings.reservations.empty}
      />
    )
  } else {
    body = reservations.map((reservation) => (
      <ReservationsTableRow
        key={reservation.id}
        reservation={reservation}
        onEdit={onEdit}
        onCancel={onCancel}
        onPreview={onPreview}
        preview={getDraftPreviewLabel(reservation, assets)}
        recipient={getReservationRecipientLabel(reservation, tags)}
      />
    ))
  }

  return (
    <DataTable
      minWidthClassName="min-w-[1200px]"
      // 8列を固定幅で割ると操作ボタンが隣の列へはみ出すため内容幅に合わせる
      tableLayout="auto"
      headerRow={
        <tr>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.reservations.table.edit}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.reservations.table.scheduledDateTime}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.reservations.table.recipient}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.reservations.table.preview}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.reservations.table.sectionCount}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.reservations.table.createdBy}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.reservations.table.createdAt}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.reservations.table.actions}
          </th>
        </tr>
      }
    >
      {body}
    </DataTable>
  )
}

type ReservationsTableRowProps = {
  reservation: ScheduledReservation
  onEdit: (reservationId: string) => void
  onCancel: (reservationId: string) => void
  onPreview: (reservation: ScheduledReservation) => void
  preview: string
  recipient: string
}

function ReservationsTableRow({
  reservation,
  onEdit,
  onCancel,
  onPreview,
  preview,
  recipient,
}: ReservationsTableRowProps) {
  return (
    <tr className="border-t border-border/60 bg-white">
      <td className="px-2 py-3 text-left whitespace-nowrap">
        <Button
          type="button"
          size="sm"
          disabled={!reservation.editable}
          title={
            reservation.editable
              ? undefined
              : sendAllMessageStrings.reservations.notEditableHint
          }
          onClick={() => onEdit(reservation.id)}
        >
          {sendAllMessageStrings.reservations.actions.edit}
        </Button>
        {/* title はホバーしないと読めないため、カードと同じ文言を行内にも出す */}
        {!reservation.editable && (
          <p className="mt-1 max-w-[14rem] whitespace-normal text-xs text-muted">
            {sendAllMessageStrings.reservations.notEditableHint}
          </p>
        )}
      </td>
      <td className="px-2 py-3 text-[11px] font-semibold text-ink sm:text-sm whitespace-nowrap">
        {formatScheduledDateTime(reservation.scheduledDateTime)}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <div className="max-w-xs truncate" title={recipient}>
          {recipient}
        </div>
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <div className="max-w-md truncate" title={preview}>
          {preview}
        </div>
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {reservation.sections.length}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {reservation.createdByName}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {moment(reservation.createdAt).format(`${DATE_FORMAT} HH:mm`)}
      </td>
      <td className="px-2 py-3 text-left whitespace-nowrap">
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onPreview(reservation)}
          >
            {sendAllMessageStrings.reservations.actions.preview}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="text-danger"
            onClick={() => onCancel(reservation.id)}
          >
            {sendAllMessageStrings.reservations.actions.cancel}
          </Button>
        </div>
      </td>
    </tr>
  )
}
